import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonAddAlt1,
  Home,
} from "@mui/icons-material";
import api from "../services/api";
import toast from "react-hot-toast";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    phone_number: "",
    address: "",
    education: "",
    role: "student",
  });

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear field-level validation once user edits that field again.
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("Profile photo must be less than 2MB");
        e.target.value = null;
        return;
      }
      setProfilePhoto(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    for (let key in formData) {
      data.append(key, formData[key]);
    }
    if (profilePhoto) {
      data.append("profile_photo", profilePhoto);
    }

    try {
      setError("");
      setFieldErrors({});

      await api.post("users/register/", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert("✅ Registered successfully!");
      navigate("/login");
    } catch (err) {
      if (err.response?.data && typeof err.response.data === "object") {
        const errors = err.response.data;
        const nextFieldErrors = {};
        let errorMsg = "";

        Object.keys(errors).forEach((key) => {
          const value = Array.isArray(errors[key])
            ? errors[key].join(", ")
            : String(errors[key]);

          if (["username", "email", "phone_number"].includes(key)) {
            nextFieldErrors[key] = value;
          }

          errorMsg += `${key}: ${value}\n`;
        });

        setFieldErrors(nextFieldErrors);
        setError(errorMsg || "❌ Registration failed. Please check the form.");
        toast.error(errorMsg || "Registration failed. Please check the form.");
      } else {
        setError("❌ Registration failed. Please try again.");
        toast.error("Registration failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-6xl w-full bg-white rounded-xl shadow-lg grid grid-cols-1 md:grid-cols-2 overflow-hidden relative">
        {/* Home Button */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white shadow hover:bg-gray-100 transition-colors"
          title="Home"
        >
          <Home fontSize="medium" className="text-blue-600" />
        </button>
        {/* SVG Illustration */}
        <div className="hidden md:flex items-center justify-center bg-blue-50 p-6">
          <img
            src="images/register.svg"
            alt="Register Illustration"
            className="max-w-md"
          />
        </div>
        {/* Register Form */}
        <div className="p-6 pt-14 md:p-10">
          <div className="mb-6 flex items-center gap-2 text-primaryText text-xl font-bold">
            <PersonAddAlt1 className="text-blue-700" />
            <Typography variant="h5">Create Student Account</Typography>
          </div>

          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
              <TextField
                label="Middle Name"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>

            <TextField
              label="Username"
              name="username"
              fullWidth
              required
              value={formData.username}
              onChange={handleChange}
              error={Boolean(fieldErrors.username)}
              helperText={fieldErrors.username}
            />

            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={handleChange}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
            />

            <TextField
              label="Password"
              name="password"
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Phone Number"
              name="phone_number"
              fullWidth
              required
              value={formData.phone_number}
              onChange={handleChange}
              error={Boolean(fieldErrors.phone_number)}
              helperText={fieldErrors.phone_number}
            />

            <TextField
              label="Address"
              name="address"
              fullWidth
              required
              value={formData.address}
              onChange={handleChange}
            />

            <TextField
              label="Education"
              name="education"
              fullWidth
              required
              value={formData.education}
              onChange={handleChange}
            />

            <div>
              <label className="block text-sm font-medium mb-1">
                Optional: Upload Profile Photo (PNG, JPG, JPEG, Max Size 2MB)
              </label>
              <input
                type="file"
                accept="image/png,image/jpg,image/jpeg"
                onChange={handlePhotoChange}
                className="block w-full text-sm border border-gray-300 rounded px-3 py-2"
              />
            </div>

            {error && (
              <pre className="text-red-600 whitespace-pre-line text-sm">
                {error}
              </pre>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="bg-blue-700 text-white hover:bg-blue-800"
            >
              Register
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
